import { useQueryClient } from '@tanstack/react-query'
import { Smartphone, Laptop, ShieldQuestion } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState, useMemo, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'

import { useWebPushNotificationSubscriptionDeleteMutation } from '@/hooks/query/notification-subscription'
import { useWebPushNotificationSubscription } from '@/hooks/use-web-push-notification-subscription'
import { USER_AGENT_DEVICE_TYPE } from '@/lib/constants'
import { getWebPushNotificationSubscriptionIdFromLocalStorage } from '@/lib/notification'
import { parseUserAgent, cn } from '@/lib/utils'
import { WebPushNotificationSubscription } from '@/types/notification'

const WebPushNotificationSubscriptionDevice = ({
  subscriptionId,
  userAgent,
  className,
}: {
  subscriptionId: WebPushNotificationSubscription['_id']
  userAgent: WebPushNotificationSubscription['userAgent']
  className?: string
}) => {
  const t = useTranslations('Notification')
  const [isUnsubscribing, setIsUnsubscribing] = useState(false)
  const [isUnsubscribed, setIsUnsubscribed] = useState(false)
  const { unsubscribeToWebPushNotification } = useWebPushNotificationSubscription()
  const queryClient = useQueryClient()
  const { mutateAsync: deleteMutateWebPushNotificationSubscription } =
    useWebPushNotificationSubscriptionDeleteMutation(queryClient)
  const { toast } = useToast()

  const parsedUserAgent = useMemo(() => {
    return parseUserAgent(userAgent)
  }, [userAgent])

  const DeviceIcon = useMemo(() => {
    const { device } = parsedUserAgent
    if (device === USER_AGENT_DEVICE_TYPE.MOBILE) {
      return Smartphone
    }
    if (device === USER_AGENT_DEVICE_TYPE.DESKTOP) {
      return Laptop
    }
    return ShieldQuestion
  }, [parsedUserAgent])

  const deviceName = useMemo(() => {
    if (parsedUserAgent.device === USER_AGENT_DEVICE_TYPE.MOBILE) {
      return t('user_agent_device_type.mobile')
    }
    if (parsedUserAgent.device === USER_AGENT_DEVICE_TYPE.DESKTOP) {
      return t('user_agent_device_type.desktop')
    }
    return ''
  }, [parsedUserAgent.device])

  const readableUserAgent = useMemo(() => {
    const { browser, version, os } = parsedUserAgent
    let result = ''
    if (browser) {
      result += browser
    }
    if (version) {
      result += ` ${version}`
    }
    if (os) {
      result += ` (${os})`
    }
    if (deviceName) {
      result += ` ${deviceName}`
    }
    return result.trim()
  }, [userAgent, deviceName])

  const unsubscribeOtherDeviceSubscription = useCallback(
    async (id: string) => {
      try {
        await deleteMutateWebPushNotificationSubscription({ id })
      } catch (error) {
        console.error(error)
        toast({
          variant: 'destructive',
          description: t('components.web_push_notification_subscription_device.messages.unsubscription_failed'),
        })
      }
    },
    [toast, deleteMutateWebPushNotificationSubscription],
  )

  const handleUnsubscribeButtonClick = useCallback(async () => {
    setIsUnsubscribing(true)
    const storedSubscriptionId = getWebPushNotificationSubscriptionIdFromLocalStorage()
    if (storedSubscriptionId === subscriptionId) {
      await unsubscribeToWebPushNotification()
    } else {
      await unsubscribeOtherDeviceSubscription(subscriptionId)
    }
    setIsUnsubscribing(false)
    setIsUnsubscribed(true)
  }, [unsubscribeOtherDeviceSubscription, subscriptionId])

  // @NOTE: technique to remove self immediately in DOM without waiting for refetches
  if (isUnsubscribed) return null

  return (
    <Card className={cn('p-4 @container/web-push-notification-subscription-device', className)}>
      <div className="flex flex-col gap-4 text-base-foreground @sm/web-push-notification-subscription-device:flex-row @sm/web-push-notification-subscription-device:items-center ">
        <div className="flex items-center gap-4">
          <DeviceIcon className="size-6 shrink-0" />
          <span
            title={readableUserAgent}
            className="line-clamp-2 font-semibold tracking-tight @sm/web-push-notification-subscription-device:line-clamp-none @sm/web-push-notification-subscription-device:truncate "
          >
            {readableUserAgent}
          </span>
        </div>

        <Button
          variant="secondary"
          className="ml-auto"
          onClick={handleUnsubscribeButtonClick}
          disabled={isUnsubscribing}
        >
          {t('components.web_push_notification_subscription_device.actions.unsubscribe')}
        </Button>
      </div>
    </Card>
  )
}
WebPushNotificationSubscriptionDevice.displayName = 'WebPushNotificationSubscriptionDevice'

export { WebPushNotificationSubscriptionDevice }
